import React from 'react';
import { processSteps } from '../mockData';
import { CheckCircle, ArrowRight } from 'lucide-react';

const Process = () => {
  return (
    <section className="relative py-32 bg-white overflow-hidden">
      {/* Creative Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-purple-50/50 via-white to-purple-50/50"></div>
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-200 rounded-full opacity-20 blur-3xl"></div>
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-pink-200 rounded-full opacity-20 blur-3xl"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Creative Section Header */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center space-x-3 bg-gradient-to-r from-purple-100 to-pink-100 px-6 py-3 rounded-full mb-6">
            <div className="w-2 h-2 bg-purple-600 rounded-full animate-pulse"></div>
            <span className="text-purple-600 font-bold text-sm uppercase tracking-widest">Simple Process</span>
            <div className="w-2 h-2 bg-pink-600 rounded-full animate-pulse" style={{animationDelay: '0.5s'}}></div>
          </div>
          <h2 className="text-5xl md:text-6xl lg:text-7xl font-black text-gray-900 mb-6 leading-tight">
            <span className="block">How It</span>
            <span className="gradient-text bg-gradient-to-r from-purple-600 to-pink-600" style={{
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>Works</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Get started in minutes with our streamlined onboarding process
          </p>
        </div>

        {/* Creative Zigzag Layout */}
        <div className="space-y-24">
          {processSteps.map((step, index) => {
            const isEven = index % 2 === 0;
            
            return (
              <div
                key={step.step}
                className={`flex flex-col ${
                  isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'
                } items-center gap-12`}
              >
                {/* Step Number Card - Creative Design */}
                <div className="lg:w-1/3 relative">
                  <div className="relative bg-gradient-to-br from-purple-600 to-pink-600 rounded-3xl p-12 shadow-2xl transform hover:scale-105 transition-all duration-300">
                    {/* Decorative Elements */}
                    <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl"></div>
                    <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full blur-xl"></div>
                    
                    <div className="relative z-10 text-center">
                      <div className="text-8xl font-black text-white/20 mb-4">{step.step}</div>
                      <div className="w-20 h-1 bg-white/50 mx-auto mb-6 rounded-full"></div>
                      <h3 className="text-3xl font-black text-white mb-4">
                        {step.title}
                      </h3>
                      <div className="flex items-center justify-center space-x-2 text-purple-100">
                        <CheckCircle size={20} />
                        <span className="text-sm font-bold">Step {step.step}</span>
                      </div>
                    </div>

                    {/* Floating Badge */}
                    <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 bg-white rounded-full px-6 py-2 shadow-xl">
                      <span className="text-purple-600 font-bold text-sm">2-3 minutes</span>
                    </div>
                  </div>
                </div>

                {/* Step Content - Creative Card */}
                <div className="lg:w-2/3">
                  <div className="bg-white rounded-3xl p-10 shadow-lg border-2 border-purple-100 hover:border-purple-300 transition-all duration-300">
                    <p className="text-2xl text-gray-700 leading-relaxed mb-6">
                      {step.description}
                    </p>
                    
                    {/* Additional Benefits */}
                    <div className="flex flex-wrap gap-3">
                      {[
                        'Fast Setup',
                        'No Hidden Fees',
                        'Expert Support'
                      ].map((benefit, idx) => (
                        <div key={idx} className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-full px-5 py-2 border border-purple-200">
                          <span className="text-purple-600 font-bold text-sm flex items-center space-x-2">
                            <CheckCircle size={16} />
                            <span>{benefit}</span>
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Connector Arrow (visible on desktop) */}
                {index < processSteps.length - 1 && (
                  <div className="hidden lg:block absolute left-1/2 transform translate-y-full mt-12">
                    <ArrowRight 
                      size={48} 
                      className={`text-purple-300 ${
                        isEven ? 'rotate-90' : '-rotate-90'
                      }`} 
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-20">
          <p className="text-2xl text-gray-600 mb-6 font-bold">Ready to get started?</p>
          <a
            href="/contact"
            className="inline-flex items-center space-x-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-10 py-5 rounded-2xl font-bold text-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
          >
            <span>Start Your Journey Now</span>
            <ArrowRight size={24} />
          </a>
        </div>
      </div>
    </section>
  );
};

export default Process;